import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import type { Project, Business } from '@/types';
import { createLogger } from '@/utils/debug';
import { projectsCollection } from '@/config/collections';

const log = createLogger('Projects');

// ===========================================
// Projects Collection Reference
// ===========================================

const projectsRef = projectsCollection(db);

// ===========================================
// Helper: Safely convert timestamp values
// ===========================================

/**
 * Safely converts a Firestore timestamp or serialized timestamp object to a Date.
 * Handles both proper Firestore Timestamps and broken serialized ones ({_seconds, _nanoseconds}).
 */
function safeToDate(value: unknown): Date | undefined {
  if (!value) return undefined;

  // If it's a proper Firestore Timestamp with toDate method
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }

  // If it's a serialized timestamp object (broken data)
  if (typeof value === 'object' && value !== null && '_seconds' in value) {
    const serialized = value as { _seconds: number; _nanoseconds?: number };
    return new Date(serialized._seconds * 1000);
  }

  // If it's already a Date
  if (value instanceof Date) {
    return value;
  }

  // If it's a string or number, try to parse it
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
}

// ===========================================
// Helper: Convert Firestore document to Project
// ===========================================

function docToProject(doc: any): Project {
  const data = doc.data();
  return {
    id: doc.id,
    source: data.source || 'monday',
    mondayItemId: data.mondayItemId,
    portalProjectCode: data.portalProjectCode,
    portalProjectUrl: data.portalProjectUrl,
    name: data.name,
    customerName: data.customerName || undefined,
    business: data.business as Business,
    projectType: data.projectType,
    status: data.status,
    closeDate: safeToDate(data.closeDate),
    contractSignedDate: safeToDate(data.contractSignedDate),
    contractAmount: data.contractAmount || 0,
    changeOrderTotal: data.changeOrderTotal || 0,
    totalRevenue: data.totalRevenue || 0,
    // Sales Rep Commission
    expectedCommission: data.expectedCommission || 0,
    commissionRuleId: data.commissionRuleId || null,
    commissionRuleDescription: data.commissionRuleDescription || undefined,
    commissionPaid: data.commissionPaid || false,
    commissionPaidDate: safeToDate(data.commissionPaidDate),
    // Canonical amount is commissionPaidAmount; keep legacy paidAmount populated
    // (with fallback) so every reader sees the real number.
    commissionPaidAmount: data.commissionPaidAmount ?? data.paidAmount ?? undefined,
    paidAmount: data.commissionPaidAmount ?? data.paidAmount ?? null,
    // Partner Commission
    partnerExpectedCommission: data.partnerExpectedCommission || 0,
    partnerCommissionDescription: data.partnerCommissionDescription || undefined,
    partnerCommissionPaid: data.partnerCommissionPaid || false,
    partnerCommissionPaidDate: safeToDate(data.partnerCommissionPaidDate),
    partnerCommissionPaidAmount: data.partnerCommissionPaidAmount ?? data.partnerPaidAmount ?? undefined,
    partnerPaidAmount: data.partnerCommissionPaidAmount ?? data.partnerPaidAmount ?? null,
    salesRepUserId: data.salesRepUserId,
    salesRepName: data.salesRepName,
    salesRepMondayPersonId: data.salesRepMondayPersonId,
    referralSourceId: data.referralSourceId,
    referralSourceName: data.referralSourceName,
    lastSyncedAt: safeToDate(data.lastSyncedAt) || new Date(),
    createdAt: safeToDate(data.createdAt) || new Date(),
    updatedAt: safeToDate(data.updatedAt) || new Date(),
  };
}

// ===========================================
// Project Query Operations
// ===========================================

/**
 * Get all projects for a business
 * Sorted by createdAt descending (newest first)
 */
export async function getProjects(business: Business): Promise<Project[]> {
  return log.operation('getProjects', async () => {
    const q = query(
      projectsRef,
      where('business', '==', business),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const projects = snapshot.docs.map(docToProject);
    log.debug('getProjects completed', { business, count: projects.length });
    return projects;
  }, { business });
}

/**
 * Get projects for a specific sales rep
 * Sorted by createdAt descending (newest first)
 */
export async function getProjectsByRep(
  business: Business,
  salesRepUserId: string
): Promise<Project[]> {
  return log.operation('getProjectsByRep', async () => {
    const q = query(
      projectsRef,
      where('business', '==', business),
      where('salesRepUserId', '==', salesRepUserId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const projects = snapshot.docs.map(docToProject);
    log.debug('getProjectsByRep completed', { business, salesRepUserId, count: projects.length });
    return projects;
  }, { business, salesRepUserId });
}

/**
 * Get closed projects within a date range
 */
export async function getClosedProjects(
  business: Business,
  startDate: Date,
  endDate: Date
): Promise<Project[]> {
  const q = query(
    projectsRef,
    where('business', '==', business),
    where('closeDate', '>=', Timestamp.fromDate(startDate)),
    where('closeDate', '<=', Timestamp.fromDate(endDate)),
    orderBy('closeDate', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToProject);
}

/**
 * Get unpaid commission projects
 */
export async function getUnpaidProjects(business: Business): Promise<Project[]> {
  const q = query(
    projectsRef,
    where('business', '==', business),
    where('commissionPaid', '==', false),
    where('expectedCommission', '>', 0),
    orderBy('expectedCommission', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToProject);
}

/**
 * Get projects referred by a specific company (referral source)
 * Sorted by createdAt descending (newest first)
 */
export async function getProjectsByReferralSource(
  referralSourceId: string
): Promise<Project[]> {
  return log.operation('getProjectsByReferralSource', async () => {
    const q = query(
      projectsRef,
      where('referralSourceId', '==', referralSourceId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const projects = snapshot.docs.map(docToProject);
    log.debug('getProjectsByReferralSource completed', { referralSourceId, count: projects.length });
    return projects;
  }, { referralSourceId });
}

/**
 * Get a single project by ID
 */
export async function getProject(id: string): Promise<Project | null> {
  return log.operation('getProject', async () => {
    const docRef = doc(projectsRef, id);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      log.debug('getProject not found', { id });
      return null;
    }

    log.debug('getProject found', { id });
    return docToProject(snapshot);
  }, { id });
}

// ===========================================
// Project Update Operations
// ===========================================

/**
 * Link a project to a referral source
 */
export async function linkReferralSource(
  projectId: string,
  referralSourceId: string,
  referralSourceName: string
): Promise<void> {
  return log.operation('linkReferralSource', async () => {
    const docRef = doc(projectsRef, projectId);
    await updateDoc(docRef, {
      referralSourceId,
      referralSourceName,
      updatedAt: serverTimestamp(),
    });
    log.info('linkReferralSource success', { projectId, referralSourceId, referralSourceName });
  }, { projectId, referralSourceId, referralSourceName });
}

/**
 * Unlink a referral source from a project
 */
export async function unlinkReferralSource(projectId: string): Promise<void> {
  return log.operation('unlinkReferralSource', async () => {
    const docRef = doc(projectsRef, projectId);
    await updateDoc(docRef, {
      referralSourceId: null,
      referralSourceName: null,
      updatedAt: serverTimestamp(),
    });
    log.info('unlinkReferralSource success', { projectId });
  }, { projectId });
}

// Paid/unpaid state is written by audited, admin/owner-only Cloud Functions —
// NOT direct client writes. This keeps field names consistent
// (commissionPaidAmount), writes an audit log, and (critically) survives the
// Portal→CRM re-sync, which only preserves CRM-owned fields it doesn't set.

/**
 * Mark commission as paid (via crm_markSalesRepCommissionPaid)
 */
export async function markCommissionPaid(
  projectId: string,
  paidAmount: number,
  _paidDate?: Date
): Promise<void> {
  return log.operation('markCommissionPaid', async () => {
    const fn = httpsCallable(getFunctions(), 'crm_markSalesRepCommissionPaid');
    await fn({ projectId, paidAmount });
    log.info('markCommissionPaid success', { projectId, paidAmount });
  }, { projectId, paidAmount });
}

/**
 * Mark commission as unpaid (via crm_markSalesRepCommissionUnpaid)
 */
export async function markCommissionUnpaid(projectId: string): Promise<void> {
  return log.operation('markCommissionUnpaid', async () => {
    const fn = httpsCallable(getFunctions(), 'crm_markSalesRepCommissionUnpaid');
    await fn({ projectId });
    log.info('markCommissionUnpaid success', { projectId });
  }, { projectId });
}

/**
 * Mark partner commission as paid (via crm_markPartnerCommissionPaid)
 */
export async function markPartnerCommissionPaid(
  projectId: string,
  paidAmount: number,
  _paidDate?: Date
): Promise<void> {
  return log.operation('markPartnerCommissionPaid', async () => {
    const fn = httpsCallable(getFunctions(), 'crm_markPartnerCommissionPaid');
    await fn({ projectId, paidAmount });
    log.info('markPartnerCommissionPaid success', { projectId, paidAmount });
  }, { projectId, paidAmount });
}

/**
 * Mark partner commission as unpaid (via crm_markPartnerCommissionUnpaid)
 */
export async function markPartnerCommissionUnpaid(projectId: string): Promise<void> {
  return log.operation('markPartnerCommissionUnpaid', async () => {
    const fn = httpsCallable(getFunctions(), 'crm_markPartnerCommissionUnpaid');
    await fn({ projectId });
    log.info('markPartnerCommissionUnpaid success', { projectId });
  }, { projectId });
}

/**
 * Get ALL projects (admin only - no business filter)
 * Sorted by createdAt descending (newest first)
 */
export async function getAllProjects(): Promise<Project[]> {
  return log.operation('getAllProjects', async () => {
    const q = query(projectsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const projects = snapshot.docs.map(docToProject);
    log.debug('getAllProjects completed', { count: projects.length });
    return projects;
  });
}

/**
 * Update partner expected commission
 */
export async function updatePartnerExpectedCommission(
  projectId: string,
  partnerExpectedCommission: number
): Promise<void> {
  return log.operation('updatePartnerExpectedCommission', async () => {
    const docRef = doc(projectsRef, projectId);
    await updateDoc(docRef, {
      partnerExpectedCommission,
      updatedAt: serverTimestamp(),
    });
    log.info('updatePartnerExpectedCommission success', { projectId, partnerExpectedCommission });
  }, { projectId, partnerExpectedCommission });
}

/**
 * Update project sales rep assignment
 */
export async function updateProjectSalesRep(
  projectId: string,
  salesRepUserId: string | null,
  salesRepName: string | null
): Promise<void> {
  return log.operation('updateProjectSalesRep', async () => {
    const docRef = doc(projectsRef, projectId);
    await updateDoc(docRef, {
      salesRepUserId,
      salesRepName,
      updatedAt: serverTimestamp(),
    });
    log.info('updateProjectSalesRep success', { projectId, salesRepUserId, salesRepName });
  }, { projectId, salesRepUserId, salesRepName });
}

// ===========================================
// Utility Functions
// ===========================================

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Get project statistics for a business
 */
export async function getProjectStats(business: Business): Promise<{
  total: number;
  totalRevenue: number;
  totalCommission: number;
  paidCommission: number;
  unpaidCommission: number;
}> {
  const projects = await getProjects(business);

  let totalRevenue = 0;
  let totalCommission = 0;
  let paidCommission = 0;

  for (const project of projects) {
    totalRevenue += project.totalRevenue;
    totalCommission += project.expectedCommission;
    if (project.commissionPaid) {
      paidCommission += project.paidAmount || project.expectedCommission;
    }
  }

  return {
    total: projects.length,
    totalRevenue,
    totalCommission,
    paidCommission,
    unpaidCommission: totalCommission - paidCommission,
  };
}
